import { LocationQueryRaw } from "vue-router";
import { ShortcutItemMap, ShortcutKey } from "@modules/app/v2/shortcut-types.ts";
import StudentGroupService from "@modules/student-group/service/student-group-service";

export const MAX_SHORTCUTS = 10;

export const SHORTCUT_KEYS = {
  MY_DRIVE: "MY_DRIVE",
  CREATE_OR_EDIT_MODULE: "CREATE_OR_EDIT_MODULE",
  STARRED_RESOURCES: "STARRED_RESOURCES",
  SHARED_WITH_ME: "SHARED_WITH_ME",
  COMMUNITIES: "COMMUNITIES",
  MOE_LIBRARY: "MOE_LIBRARY",
  COMMUNITY_GALLERY: "COMMUNITY_GALLERY",
  FIND_RESOURCES: "FIND_RESOURCES",
  ASSIGNMENTS: "ASSIGNMENTS",
  ASSESSMENTS: "ASSESSMENTS",
  NOTIFICATIONS: "NOTIFICATIONS",
  CREATE_ANNOUNCEMENT: "CREATE_ANNOUNCEMENT",
  LEARNING_PROGRESS: "LEARNING_PROGRESS",
  PLANNER: "PLANNER",
  MANAGE_STUDENT_PASSWORD_OR_PASSCODE: "MANAGE_STUDENT_PASSWORD_OR_PASSCODE",
  MY_SUBJECTS: "MY_SUBJECTS",
  SWITCH_TO_STUDENT_ACCOUNT: "SWITCH_TO_STUDENT_ACCOUNT",
  SELF_STUDY: "SELF_STUDY",
  ALS: "ALS",
  PROFESSIONAL_DEVELOPMENT_GROUPS: "PROFESSIONAL_DEVELOPMENT_GROUPS",
  STAFF_GROUPS: "STAFF_GROUPS",
  STUDENT_GROUPS: "STUDENT_GROUPS",
  ADMIN: "ADMIN",
  ACCOUNT: "ACCOUNT",
  NOTEBOOK: "NOTEBOOK",
  USER_RECENTLY_ACCESSED: "USER_RECENTLY_ACCESSED",
  HOME: "HOME",
  APP_LIBRARY: "APP_LIBRARY",
  EXTERNAL_RESOURCES: "EXTERNAL_RESOURCES",
  GROUPS: "GROUPS",
  SWITCH_TO_TEACHER_ACCOUNT: "SWITCH_TO_TEACHER_ACCOUNT",
} as const;

export const LINK_TYPES = { HYPERLINK: "HYPERLINK", EVENT: "EVENT" } as const;

export const CATEGORIES = {
  SHORTCUT_AVAILABLE: "SHORTCUT_AVAILABLE",
  PERSONAL_WORKSPACE: "PERSONAL_WORKSPACE",
  CONTENT_AND_RESOURCES: "CONTENT_AND_RESOURCES",
  TEACHING_AND_ASSESSMENTS: "TEACHING_AND_ASSESSMENTS",
  GROUPS_AND_COLLABORATION: "GROUPS_AND_COLLABORATION",
  STUDY_TOOLS_AND_RESOURCES: "STUDY_TOOLS_AND_RESOURCES",
  ACCOUNT_MANAGEMENT: "ACCOUNT_MANAGEMENT",
} as const;

export const ROLE_CATEGORIES_MAP = {
  TEACHER_CATEGORY: [
    CATEGORIES.PERSONAL_WORKSPACE,
    CATEGORIES.CONTENT_AND_RESOURCES,
    CATEGORIES.TEACHING_AND_ASSESSMENTS,
    CATEGORIES.GROUPS_AND_COLLABORATION,
    CATEGORIES.ACCOUNT_MANAGEMENT,
  ],
  STUDENT_CATEGORY: [
    CATEGORIES.PERSONAL_WORKSPACE,
    CATEGORIES.STUDY_TOOLS_AND_RESOURCES,
    CATEGORIES.ACCOUNT_MANAGEMENT,
  ],
  NON_TEACHER_STUDENT_CATEGORY: [CATEGORIES.ACCOUNT_MANAGEMENT],
} as const;

export const SHORTCUT_ITEM_MAP: ShortcutItemMap = {
  MY_DRIVE: {
    title: "app_side_nav_shortcuts_item_my_drive_title",
    description: "app_side_nav_shortcuts_item_my_drive_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "MyDrive32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
  },
  CREATE_OR_EDIT_MODULE: {
    title: "app_side_nav_shortcuts_item_create_edit_module_title",
    description: "app_side_nav_shortcuts_item_create_edit_module_description",
    linkType: LINK_TYPES.EVENT,
    icon: "CreateEditModule32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
    ariaLabel: "shortcut_create_or_edit_module_aria_label",
  },
  STARRED_RESOURCES: {
    title: "app_side_nav_shortcuts_item_starred_resources_title",
    description: "app_side_nav_shortcuts_item_starred_resources_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "StarredResources32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
  },
  SHARED_WITH_ME: {
    title: "app_side_nav_shortcuts_item_shared_with_me_title",
    description: "app_side_nav_shortcuts_item_shared_with_me_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "TeacherSharingEnabled32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
  },
  COMMUNITIES: {
    title: "app_side_nav_shortcuts_item_communities_title",
    description: "app_side_nav_shortcuts_item_communities_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Community32",
    category: [CATEGORIES.GROUPS_AND_COLLABORATION],
    isAppPermissionRequired: false,
  },
  MOE_LIBRARY: {
    title: "app_side_nav_shortcuts_item_moe_library_title",
    description: "app_side_nav_shortcuts_item_moe_library_description",
    linkType: LINK_TYPES.EVENT,
    icon: "MOELibrary32",
    category: [CATEGORIES.CONTENT_AND_RESOURCES, CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
    ariaLabel: "shortcut_moe_library_aria_label",
  },
  COMMUNITY_GALLERY: {
    title: "app_side_nav_shortcuts_item_community_gallery_title",
    description: "app_side_nav_shortcuts_item_community_gallery_description",
    linkType: LINK_TYPES.EVENT,
    icon: "CommunityGallery32",
    category: [CATEGORIES.CONTENT_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  FIND_RESOURCES: {
    title: "app_side_nav_shortcuts_item_find_resources_title",
    description: "app_side_nav_shortcuts_item_find_resources_teacher_description",
    studentDescription: "app_side_nav_shortcuts_item_find_resources_student_description",
    linkType: LINK_TYPES.EVENT,
    icon: "ExploreResources32",
    category: [CATEGORIES.CONTENT_AND_RESOURCES, CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  ASSIGNMENTS: {
    title: "app_side_nav_shortcuts_item_assignments_title",
    description: "app_side_nav_shortcuts_item_assignments_teacher_description",
    studentDescription: "app_side_nav_shortcuts_item_assignments_student_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Assignment32",
    category: [CATEGORIES.TEACHING_AND_ASSESSMENTS, CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  ASSESSMENTS: {
    title: "app_side_nav_shortcuts_item_assessments_title",
    description: "app_side_nav_shortcuts_item_assessments_teacher_description",
    studentDescription: "app_side_nav_shortcuts_item_assessments_student_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Assessment32",
    category: [CATEGORIES.TEACHING_AND_ASSESSMENTS, CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  NOTIFICATIONS: {
    title: "app_side_nav_shortcuts_item_notifications_title",
    description: "app_side_nav_shortcuts_item_notifications_description",
    linkType: LINK_TYPES.EVENT,
    icon: "Notification32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
  },
  CREATE_ANNOUNCEMENT: {
    title: "app_side_nav_shortcuts_item_create_new_announcement_title",
    description: "app_side_nav_shortcuts_item_create_new_announcement_description",
    linkType: LINK_TYPES.EVENT,
    icon: "CreateNewAnnouncement32",
    category: [CATEGORIES.GROUPS_AND_COLLABORATION],
    isAppPermissionRequired: false,
  },
  LEARNING_PROGRESS: {
    title: "app_side_nav_shortcuts_item_learning_progress_title",
    description: "app_side_nav_shortcuts_item_learning_progress_teacher_description",
    studentDescription: "app_side_nav_shortcuts_item_learning_progress_student_description",
    linkType: LINK_TYPES.EVENT,
    icon: "LearningProgress32",
    category: [CATEGORIES.TEACHING_AND_ASSESSMENTS, CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  PLANNER: {
    title: "app_side_nav_shortcuts_item_planner_title",
    description: "app_side_nav_shortcuts_item_planner_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Planner32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
  },
  MANAGE_STUDENT_PASSWORD_OR_PASSCODE: {
    title: "app_side_nav_shortcuts_item_manage_student_password_passcode_title",
    description: "app_side_nav_shortcuts_item_manage_student_password_passcode_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Passcode32",
    category: [CATEGORIES.ACCOUNT_MANAGEMENT],
    isAppPermissionRequired: false,
    ariaLabel: "shortcut_manage_student_password_passcode_aria_label",
  },
  MY_SUBJECTS: {
    title: "app_side_nav_shortcuts_item_my_subjects_title",
    description: "app_side_nav_shortcuts_item_my_subjects_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "MySubjects32",
    category: [CATEGORIES.ACCOUNT_MANAGEMENT],
    isAppPermissionRequired: false,
  },
  SWITCH_TO_STUDENT_ACCOUNT: {
    title: "app_side_nav_shortcuts_item_switch_to_student_account_title",
    description: "app_side_nav_shortcuts_item_switch_to_student_account_description",
    linkType: LINK_TYPES.EVENT,
    icon: "SwitchToStudentAccount32",
    category: [CATEGORIES.ACCOUNT_MANAGEMENT],
    isAppPermissionRequired: false,
  },
  SELF_STUDY: {
    title: "app_side_nav_shortcuts_item_self_study_title",
    description: "app_side_nav_shortcuts_item_self_study_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "SelfStudy32",
    category: [CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  ALS: {
    title: "app_side_nav_shortcuts_item_als_title",
    description: "app_side_nav_shortcuts_item_als_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "AdaptiveLearning32",
    category: [CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: true,
  },
  PROFESSIONAL_DEVELOPMENT_GROUPS: {
    title: "app_side_nav_shortcuts_item_professional_development_group_title",
    description: "app_side_nav_shortcuts_item_professional_development_group_description",
    linkType: LINK_TYPES.EVENT,
    icon: "ProfessionalDevelopmentGroups32",
    category: [CATEGORIES.GROUPS_AND_COLLABORATION],
    isAppPermissionRequired: false,
  },
  STAFF_GROUPS: {
    title: "app_side_nav_shortcuts_item_staff_group_title",
    description: "app_side_nav_shortcuts_item_staff_group_description",
    linkType: LINK_TYPES.EVENT,
    icon: "TeacherGroups32",
    category: [CATEGORIES.GROUPS_AND_COLLABORATION],
    isAppPermissionRequired: false,
  },
  STUDENT_GROUPS: {
    title: "app_side_nav_shortcuts_item_student_group_title",
    description: "app_side_nav_shortcuts_item_student_group_teacher_description",
    studentDescription: "app_side_nav_shortcuts_item_student_group_student_description",
    linkType: LINK_TYPES.EVENT,
    icon: "StudentGroups32",
    category: [CATEGORIES.GROUPS_AND_COLLABORATION, CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  ADMIN: {
    title: "app_side_nav_shortcuts_item_admin_title",
    description: "app_side_nav_shortcuts_item_admin_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Admin32",
    category: [CATEGORIES.ACCOUNT_MANAGEMENT],
    isAppPermissionRequired: false,
  },
  ACCOUNT: {
    title: "app_side_nav_shortcuts_item_account_title",
    description: "app_side_nav_shortcuts_item_account_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "User32",
    category: [CATEGORIES.ACCOUNT_MANAGEMENT],
    isAppPermissionRequired: false,
  },
  NOTEBOOK: {
    title: "app_side_nav_shortcuts_item_notebook_title",
    description: "app_side_nav_shortcuts_item_notebook_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "MyNotebooks32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
  },
  USER_RECENTLY_ACCESSED: {
    title: "app_side_nav_shortcuts_item_user_recently_accessed_title",
    description: "app_side_nav_shortcuts_item_user_recently_accessed_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "ContinueWhereYouLeftOff32",
    category: [CATEGORIES.PERSONAL_WORKSPACE],
    isAppPermissionRequired: false,
  },
  HOME: {
    title: "app_side_nav_shortcuts_item_home_title",
    description: "",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Home32",
    category: [CATEGORIES.SHORTCUT_AVAILABLE],
    isAppPermissionRequired: false,
  },
  APP_LIBRARY: {
    title: "app_side_nav_shortcuts_item_app_library_title",
    description: "app_side_nav_shortcuts_item_app_library_description",
    linkType: LINK_TYPES.EVENT,
    icon: "App32",
    category: [CATEGORIES.CONTENT_AND_RESOURCES, CATEGORIES.STUDY_TOOLS_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  EXTERNAL_RESOURCES: {
    title: "app_side_nav_shortcuts_item_external_resources_title",
    description: "app_side_nav_shortcuts_item_external_resources_description",
    linkType: LINK_TYPES.EVENT,
    icon: "Website32",
    category: [CATEGORIES.CONTENT_AND_RESOURCES],
    isAppPermissionRequired: false,
  },
  GROUPS: {
    title: "app_side_nav_shortcuts_item_groups_title",
    description: "app_side_nav_shortcuts_item_groups_description",
    linkType: LINK_TYPES.HYPERLINK,
    icon: "Class32",
    category: [CATEGORIES.GROUPS_AND_COLLABORATION],
    isAppPermissionRequired: false,
  },
  SWITCH_TO_TEACHER_ACCOUNT: {
    title: "app_side_nav_shortcuts_item_switch_to_teacher_account_title",
    description: "app_side_nav_shortcuts_item_switch_to_teacher_account_description",
    linkType: LINK_TYPES.EVENT,
    icon: "SwitchToTeacherAccount32",
    category: [CATEGORIES.ACCOUNT_MANAGEMENT],
    isAppPermissionRequired: false,
  },
} as const;

export const SHORTCUT_ROUTE_QUERY_MAP: Partial<Record<ShortcutKey, LocationQueryRaw>> = {
  [SHORTCUT_KEYS.PROFESSIONAL_DEVELOPMENT_GROUPS]:
    StudentGroupService.getProfessionalDevelopmentGroupRouteQuery(),
  [SHORTCUT_KEYS.STAFF_GROUPS]: StudentGroupService.getStaffGroupRouteQuery(),
  [SHORTCUT_KEYS.STUDENT_GROUPS]: StudentGroupService.getStudentGroupRouteQuery(),
} as const;

export const TeacherShortcuts: ShortcutKey[] = [
  // Shortcut Available
  SHORTCUT_KEYS.HOME,
  // Personal Workspace
  SHORTCUT_KEYS.USER_RECENTLY_ACCESSED,
  SHORTCUT_KEYS.CREATE_OR_EDIT_MODULE,
  SHORTCUT_KEYS.MY_DRIVE,
  SHORTCUT_KEYS.NOTIFICATIONS,
  SHORTCUT_KEYS.PLANNER,
  SHORTCUT_KEYS.SHARED_WITH_ME,
  SHORTCUT_KEYS.STARRED_RESOURCES,
  // Content and Resources
  SHORTCUT_KEYS.APP_LIBRARY,
  SHORTCUT_KEYS.COMMUNITY_GALLERY,
  SHORTCUT_KEYS.EXTERNAL_RESOURCES,
  SHORTCUT_KEYS.FIND_RESOURCES,
  SHORTCUT_KEYS.MOE_LIBRARY,
  // Teaching And Assessments
  SHORTCUT_KEYS.ASSESSMENTS,
  SHORTCUT_KEYS.ASSIGNMENTS,
  SHORTCUT_KEYS.LEARNING_PROGRESS,
  // Groups And Collaboration
  SHORTCUT_KEYS.COMMUNITIES,
  SHORTCUT_KEYS.CREATE_ANNOUNCEMENT,
  SHORTCUT_KEYS.GROUPS,
  SHORTCUT_KEYS.PROFESSIONAL_DEVELOPMENT_GROUPS,
  SHORTCUT_KEYS.STAFF_GROUPS,
  SHORTCUT_KEYS.STUDENT_GROUPS,
  // Account Management
  SHORTCUT_KEYS.ACCOUNT,
  SHORTCUT_KEYS.ADMIN,
  SHORTCUT_KEYS.MANAGE_STUDENT_PASSWORD_OR_PASSCODE,
  SHORTCUT_KEYS.MY_SUBJECTS,
  SHORTCUT_KEYS.SWITCH_TO_STUDENT_ACCOUNT,
];

export const StudentShortcuts: ShortcutKey[] = [
  // Shortcut Available
  SHORTCUT_KEYS.HOME,
  // Personal Workspace
  SHORTCUT_KEYS.USER_RECENTLY_ACCESSED,
  SHORTCUT_KEYS.MY_DRIVE,
  SHORTCUT_KEYS.NOTEBOOK,
  SHORTCUT_KEYS.NOTIFICATIONS,
  SHORTCUT_KEYS.PLANNER,
  SHORTCUT_KEYS.STARRED_RESOURCES,
  // Study Tools And Resources
  SHORTCUT_KEYS.ALS,
  SHORTCUT_KEYS.APP_LIBRARY,
  SHORTCUT_KEYS.ASSESSMENTS,
  SHORTCUT_KEYS.ASSIGNMENTS,
  SHORTCUT_KEYS.LEARNING_PROGRESS,
  SHORTCUT_KEYS.MOE_LIBRARY,
  SHORTCUT_KEYS.SELF_STUDY,
  SHORTCUT_KEYS.STUDENT_GROUPS,
  // Account Management
  SHORTCUT_KEYS.ACCOUNT,
  SHORTCUT_KEYS.MY_SUBJECTS,
];

export const NonTeacherNonStudentShortcuts: ShortcutKey[] = [
  // Shortcut Available
  SHORTCUT_KEYS.HOME,
  // Account Management
  SHORTCUT_KEYS.ADMIN,
];

export const TeacherDefaultShortcuts: ShortcutKey[] = [
  SHORTCUT_KEYS.HOME,
  SHORTCUT_KEYS.MY_DRIVE,
  SHORTCUT_KEYS.CREATE_OR_EDIT_MODULE,
  SHORTCUT_KEYS.STUDENT_GROUPS,
  SHORTCUT_KEYS.MOE_LIBRARY,
  SHORTCUT_KEYS.COMMUNITY_GALLERY,
  SHORTCUT_KEYS.FIND_RESOURCES,
  SHORTCUT_KEYS.ASSIGNMENTS,
  SHORTCUT_KEYS.ADMIN,
  SHORTCUT_KEYS.MANAGE_STUDENT_PASSWORD_OR_PASSCODE,
];

export const StudentDefaultShortcuts: ShortcutKey[] = [
  SHORTCUT_KEYS.HOME,
  SHORTCUT_KEYS.ASSIGNMENTS,
  SHORTCUT_KEYS.STUDENT_GROUPS,
  SHORTCUT_KEYS.NOTEBOOK,
  SHORTCUT_KEYS.MOE_LIBRARY,
  SHORTCUT_KEYS.MY_SUBJECTS,
];

export const NonTeacherNonStudentDefaultShortcuts: ShortcutKey[] = [
  SHORTCUT_KEYS.HOME,
  SHORTCUT_KEYS.ADMIN,
];
